import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { useIntl } from '../../util/reactIntl';
import { useWishlist } from '../../context/WishlistContext';
import IconSpinner from '../IconSpinner/IconSpinner';

import css from './WishlistButton.module.css';

const HeartIcon = ({ filled, className }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill={filled ? '#b45a3a' : 'none'}
    stroke={filled ? '#b45a3a' : '#7a7268'}
    strokeWidth={filled ? '2' : '1.5'}
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

/**
 * Heart toggle wired to Firebase wishlist (optimistic UI + loading state).
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing Sharetribe listing entity (required)
 * @param {string} [props.className] button className
 * @param {boolean} [props.requireAuth=true] redirect guests to login when true
 * @param {boolean} [props.allowAnonymous=false] store in localStorage for guests
 * @param {'sm'|'md'|'lg'} [props.size='md']
 */
const WishlistButton = props => {
  const {
    listing,
    className,
    requireAuth = true,
    allowAnonymous = false,
    size = 'md',
    iconClassName,
  } = props;

  const intl = useIntl();
  const { isWishlisted, isSyncing, toggleWishlist, firebaseEnabled } = useWishlist();

  const listingId = listing?.id?.uuid;
  const wishlisted = isWishlisted(listingId);
  const syncing = isSyncing(listingId);

  if (!listingId || !firebaseEnabled) {
    return null;
  }

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(listing, { requireAuth: requireAuth && !allowAnonymous });
  };

  const labelId = wishlisted ? 'ListingCard.removeFromWishlist' : 'ListingCard.saveToWishlist';

  return (
    <button
      type="button"
      className={classNames(css.root, css[`size_${size}`], className, {
        [css.active]: wishlisted,
        [css.syncing]: syncing,
      })}
      aria-label={intl.formatMessage({ id: labelId })}
      aria-pressed={wishlisted}
      disabled={syncing}
      onClick={handleClick}
    >
      {syncing ? (
        <IconSpinner rootClassName={css.spinner} />
      ) : (
        <HeartIcon filled={wishlisted} className={iconClassName} />
      )}
    </button>
  );
};

WishlistButton.propTypes = {
  listing: PropTypes.object.isRequired,
  className: PropTypes.string,
  requireAuth: PropTypes.bool,
  allowAnonymous: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  iconClassName: PropTypes.string,
};

export default WishlistButton;
